import { useEffect, useState } from 'react';

import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import ModalWithFullScreenBg from 'shared/components/ui/Modal/ModalWithFullScreenBg';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { useAccount } from 'shared/hooks/use-web3';

import bonuses from 'assets/png/bonuses-desktop.png?format=webp;avif&as=picture';
import ShareIcon from 'assets/share.svg?react';

const ShareGameModal = ({
  open,
  setOpen,
  isMobile,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile?: boolean;
}) => {
  const { address } = useAccount();
  const [showToast, setShowToast] = useState(false);

  const handleShareClick = async () => {
    const currentUrl = window.location.href;
    const streamUrl = `${currentUrl.replace('room', 'stream')}?ref=${address}`;

    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: 'Game Replay',
          text: 'Check out this game replay!',
          url: streamUrl,
        });
      } catch (error) {
        // If sharing is cancelled or fails, fall back to clipboard
        await navigator.clipboard.writeText(streamUrl);
      }
    } else {
      await navigator.clipboard.writeText(streamUrl);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 1000);
    }
  };

  useEffect(() => {
    if (open) {
      audioManager.playSound(AUDIO_TYPES.ROUND_WIN);
    }
  }, [open]);

  return (
    <ModalWithFullScreenBg
      open={open}
      className="bg-background bg-opacity-50"
      childrenClassName="bg-share-game-modal-border p-[2px] max-w-[38.125rem]"
    >
      <div className="h-full w-full rounded-3xl bg-share-game-modal-gradient">
        <div className="flex flex-col p-4 sm:px-6 sm:py-6 sm:pt-8">
          <OptimizedImage
            imageData={bonuses}
            alt="bonuses"
            className="h-[100px] w-[120px] self-center sm:h-[300px] sm:w-[360px]"
          />
          <h3 className="mb-5 bg-gradient-to-r from-[#FFF] to-[#FFC000] bg-clip-text font-poppins text-[16px] font-bold leading-[20px] text-transparent sm:my-6 sm:text-[36px] sm:leading-[44px]">
            Share Game Replay!
          </h3>
          <p className="font-poppins text-[10px] font-normal leading-[12px] text-[#F7F7F7] sm:text-[20px] sm:leading-[24px]">
            Share your game replay with friends and earn exclusive rewards! Every time a new player
            joins through your shared replay link, you receive a referral bonus.
          </p>
          <div className="my-3 h-[2px] w-full bg-indigo sm:my-6"></div>
          <p className="mb-6 font-poppins text-[9px] font-normal leading-[12px] text-light-grape sm:text-[16px] sm:leading-[20px]">
            Start sharing now and build your gaming network! 🚀
          </p>
          <SecondaryBtn
            onClick={handleShareClick}
            text="common.shareGame"
            svg={<ShareIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
            childClassName="min-h-12"
          />
          <button
            onClick={() => setOpen(false)}
            className="mt-6 font-poppins text-[10px] font-normal leading-[12px] text-light-grape sm:text-[16px] sm:leading-[20px]"
          >
            No, thanks
          </button>

          {/* Toast Notification */}
          <div
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 transform rounded-lg bg-indigo px-6 py-3 font-poppins text-[16px] text-white transition-opacity duration-300 ${showToast ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          >
            Link copied
          </div>
        </div>
      </div>
    </ModalWithFullScreenBg>
  );
};

export default ShareGameModal;
